import { useEffect, useRef } from "react";
import { levels } from "@/components/listen/LedVisualizer";

import { SCENES } from "@/components/listen/deckScenes";

export const DECK_MODES = ["SPEC", "PEAK", "SCOPE", "VU", "MIRROR", "DOLPHIN", "LOWRIDER", "SUNSET", "CITY", "WARP", "FIRE", "AUTO"];
const CYCLE = DECK_MODES.filter((m) => m !== "AUTO");

const readVar = (el, name, fallback) => getComputedStyle(el).getPropertyValue(name).trim() || fallback;

// Small LCD-style visualizer inside the car deck, with several modes.
export const DeckVisualizer = ({ clock, audioApi, playing, mode, onAutoMode, className = "w-full h-24 sm:h-28" }) => {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const live = useRef({});
  live.current = { audioApi, playing, mode, onAutoMode };

  useEffect(() => {
    const el = wrap.current, cv = canvas.current, ctx = cv.getContext("2d");
    let raf, w = 0, h = 0, dpr = 1, freq, wave, smooth = [], peaks = [], vu = [0, 0], vuPeak = [0, 0], autoIdx = 0, lastCycleBeat = null;
    const sceneState = {};
    const size = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = el.clientWidth; h = el.clientHeight;
      cv.width = w * dpr; cv.height = h * dpr;
      cv.style.width = `${w}px`; cv.style.height = `${h}px`;
    };
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (!w) return;
      const { audioApi: api, playing: on, mode: m, onAutoMode: report } = live.current;
      const analyser = on ? api?.analyser?.() : null;
      if (analyser) {
        if (!freq || freq.length !== analyser.frequencyBinCount) { freq = new Uint8Array(analyser.frequencyBinCount); wave = new Uint8Array(analyser.fftSize); }
        analyser.getByteFrequencyData(freq);
        analyser.getByteTimeDomainData(wave);
      }
      const { pulse, beat, frac } = clock.pulse(now);
      let mode = m;
      if (m === "AUTO") {
        if (lastCycleBeat === null) lastCycleBeat = beat;
        if (beat - lastCycleBeat >= 16) { lastCycleBeat = beat; autoIdx = (autoIdx + 1) % CYCLE.length; }
        mode = CYCLE[autoIdx];
        report?.(mode);
      }
      const c1 = readVar(el, "--t-label", "#ffe600"), c2 = readVar(el, "--t-title2", "#ffb800");
      const energy = on ? 1 : 0.6;
      const bands = 24;
      const lv = levels(bands, analyser, freq, pulse, beat, energy, 1);
      for (let i = 0; i < bands; i++) {
        smooth[i] = lv[i] > (smooth[i] || 0) ? lv[i] : (smooth[i] || 0) * 0.88 + lv[i] * 0.12;
        peaks[i] = Math.max((peaks[i] || 0) - 0.008, smooth[i]);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.shadowColor = c1; ctx.shadowBlur = 6;
      const segH = 4, gap = 2;
      const grad = ctx.createLinearGradient(0, h, 0, 0);
      grad.addColorStop(0, c1); grad.addColorStop(0.75, c1); grad.addColorStop(1, c2);

      if (SCENES[mode]) {
        ctx.shadowBlur = 4;
        SCENES[mode](ctx, { w, h, t: now / 1000, pulse: pulse * energy, beat, frac, lv: smooth, bass: smooth.slice(0, 4).reduce((a, b) => a + b, 0) / 4, c1, c2, s: sceneState });
      } else if (mode === "SPEC" || mode === "PEAK" || mode === "MIRROR") {
        const bw = w / bands;
        for (let i = 0; i < bands; i++) {
          const x = i * bw + 2, bwid = bw - 4;
          if (mode === "SPEC") {
            const segs = Math.floor((smooth[i] * h) / (segH + gap));
            ctx.fillStyle = grad;
            for (let s = 0; s < segs; s++) ctx.fillRect(x, h - (s + 1) * (segH + gap), bwid, segH);
          } else if (mode === "PEAK") {
            ctx.fillStyle = c1; ctx.globalAlpha = 0.55;
            const bh = smooth[i] * h;
            ctx.fillRect(x + bwid * 0.3, h - bh, bwid * 0.4, bh);
            ctx.globalAlpha = 1; ctx.fillStyle = c2;
            ctx.fillRect(x, h - peaks[i] * h - 3, bwid, 3);
          } else {
            const bh = (smooth[i] * h) / 2;
            ctx.fillStyle = grad;
            const segs = Math.floor(bh / (segH + gap));
            for (let s = 0; s < segs; s++) {
              ctx.fillRect(x, h / 2 - (s + 1) * (segH + gap), bwid, segH);
              ctx.globalAlpha = 0.5; ctx.fillRect(x, h / 2 + s * (segH + gap) + gap, bwid, segH); ctx.globalAlpha = 1;
            }
          }
        }
      } else if (mode === "SCOPE") {
        ctx.strokeStyle = c1; ctx.globalAlpha = 0.18; ctx.lineWidth = 1;
        for (let gx = 0; gx <= w; gx += w / 8) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke(); }
        ctx.beginPath(); ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2); ctx.stroke();
        ctx.globalAlpha = 1; ctx.lineWidth = 2; ctx.beginPath();
        const pts = 120;
        for (let k = 0; k <= pts; k++) {
          const v = analyser ? (wave[Math.floor((k / pts) * (wave.length - 1))] - 128) / 128 * 1.8
            : (Math.sin(k * 0.35 + now / 120) * 0.6 + Math.sin(k * 0.11 - now / 400) * 0.4) * (0.2 + 0.8 * pulse) * energy;
          const y = h / 2 - Math.max(-1, Math.min(1, v)) * h * 0.45;
          k ? ctx.lineTo((k / pts) * w, y) : ctx.moveTo(0, y);
        }
        ctx.stroke();
      } else if (mode === "VU") {
        const lvl = [lv.slice(0, 12).reduce((a, b) => a + b, 0) / 12, lv.slice(6, 18).reduce((a, b) => a + b, 0) / 12];
        const segs = 28, sw = (w - 40) / segs;
        ["L", "R"].forEach((ch, r) => {
          vu[r] = lvl[r] * 1.3 > vu[r] ? Math.min(1, lvl[r] * 1.3) : vu[r] * 0.92;
          vuPeak[r] = Math.max(vuPeak[r] - 0.004, vu[r]);
          const y = h * (r ? 0.58 : 0.18), mh = h * 0.24;
          ctx.fillStyle = c1; ctx.font = "bold 11px monospace"; ctx.textBaseline = "middle"; ctx.fillText(ch, 4, y + mh / 2);
          for (let s = 0; s < segs; s++) {
            const lit = s / segs < vu[r], hot = s > segs * 0.78, pk = Math.floor(vuPeak[r] * segs) === s;
            ctx.globalAlpha = lit || pk ? 1 : 0.12;
            ctx.fillStyle = hot ? c2 : c1;
            ctx.fillRect(24 + s * sw, y, sw - 2, mh);
          }
          ctx.globalAlpha = 1;
        });
      }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;
    };
    size();
    raf = requestAnimationFrame(frame);
    const ro = new ResizeObserver(size);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [clock]);

  return (
    <div ref={wrap} className={className} data-testid="deck-visualizer">
      <canvas ref={canvas} aria-label="Deck visualizer" />
    </div>
  );
};
